import { useCallback, useEffect, useId, useState } from 'react';
import {
  InputLabel,
  MenuItem,
  FormControl,
  FormHelperText,
  Select,
  ListItemButton,
  List,
  SelectChangeEvent,
  useTheme,
  Drawer,
  useMediaQuery
} from '@mui/material';

import styles from '@components/DynamicDropDown.module.css';

interface DynamicDropdownProps<T> {
  id?: string;
  label?: string;
  onChange?: (value: T) => void;
  width?: string;
  /** When true paints the input red */
  error?: boolean;
  /** When true includes an asterisk indicated field input is required */
  required?: boolean;
  /** Text displayed under the input, can be used as a hint or error feedback when props.error = true */
  helperText?: string;
  /** Options for the Select dropdown */
  options: T[];
  /** Selected value when the Select initially renders */
  defaultValue?: T | null;
  /** String value of the object property name that will be used for display to the user */
  optionKey?: string;
  /** Callback used for rendering the options key */
  renderOptionKey?: (option: T) => string;
  /** Display text if the Array passed into props.options is empty */
  emptyText?: string;
  /** Text Align of the selected text */
  textAlign?: 'center' | 'left' | 'right';
}

/**
 * Styled Select List Component that renders a dropdown list of T. When there is only a single option in the array of items
 * then the dropdown component appears as plaintext.
 */
export default function DynamicDropDown<T>(props: DynamicDropdownProps<T>) {
  const { options, onChange, defaultValue, optionKey } = props;
  const generatedId = useId();
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(null);

  useEffect(() => {
    // Set the default value if we were given one
    if (defaultValue) {
      // If we have a property, than this is an object and not a primitive
      if (optionKey) {
        const objArray = options as Record<string, unknown>[];
        const defaultObject = defaultValue as Record<string, unknown>;
        setSelectedIndex(objArray.map((x) => x[optionKey]).indexOf(defaultObject[optionKey]));
      } else {
        setSelectedIndex(options.indexOf(defaultValue));
      }
    }
  }, [defaultValue, optionKey, options]);

  useEffect(() => {
    // When only one option exists, select it by default
    if (options.length === 1 && selectedIndex === undefined) {
      setSelectedIndex(0);
      if (onChange) {
        onChange(options[0]);
      }
    }
  }, [options, onChange, selectedIndex]);

  /** Method used for extracting the Text from the Select Option */
  const getOptionText = useCallback(
    (option: T): string => {
      if (typeof option === 'string') {
        return option;
      }

      if (typeof option === 'number') {
        return String(option);
      }

      if (props.renderOptionKey) {
        return props.renderOptionKey(option);
      }

      const obj = option as Record<string, unknown>;
      if (props.optionKey && props.optionKey in obj) {
        return String(obj[props.optionKey]);
      }

      throw new Error(`DynamicDropDown Error: Could not retrieve option text for option ${String(option)}`);
    },
    [props]
  );

  const handleChange = (event: SelectChangeEvent) => {
    const index = Number(event.target.value);
    setSelectedIndex(index);

    // Invoke callers onChange function
    if (onChange) {
      onChange(options[index]);
    }
  };

  // Specific styles/behavior for Single or Empty Options
  const singleOrEmptyProps = {
    disabled: true,
    inputProps: {
      IconComponent: () => null
    }
  };

  if (options.length === 0) {
    return <></>;
  }

  if (isMobile) {
    return (
      <>
        <FormControl
          fullWidth
          error={props.error}
          required={props.required}
          size='small'>
          {props.label && <InputLabel id={`${generatedId}-label`}>{props.label}</InputLabel>}
          <Select
            id={generatedId}
            labelId={`${generatedId}-label`}
            size='small'
            label={props.label}
            value={selectedIndex !== undefined ? String(selectedIndex) : ''}
            open={isOpen}
            displayEmpty
            renderValue={(value: string) => (value ? getOptionText(options[Number(value)]) : props.emptyText)}
            onClose={() => {
              setIsOpen(false);
            }}
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              textAlign: props.textAlign ?? 'left',
              '& fieldset': options.length === 1 ? { border: 'none' } : {},
              '& .Mui-disabled.MuiSelect-select.MuiInputBase-input':
                options.length === 1
                  ? {
                      WebkitTextFillColor: theme.palette.text.primary,
                      paddingRight: '2px'
                    }
                  : {},
              backgroundColor: options.length > 1 ? theme.palette.common.white : 'default',
              width: props.width,
              color: !defaultValue ? theme.palette.grey[500] : theme.palette.common.black,
              fontStyle: !defaultValue ? 'italic' : 'none'
            }}></Select>
        </FormControl>
        <Drawer
          anchor={'bottom'}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
          }}
          className={styles['drawer']}
          slotProps={{
            paper: { className: styles['drawer-paper'] }
          }}>
          <div className={styles['container']}>
            <List
              dense
              disablePadding
              className={styles.actions}>
              {options.map((_, index) => (
                <ListItemButton
                  key={`${generatedId}-option-${String(index)}`}
                  onClick={() => {
                    setSelectedIndex(index);

                    // Invoke callers onChange function
                    if (onChange) {
                      onChange(options[index]);
                    }
                    setAnchorEl(null);
                  }}>
                  {getOptionText(options[index])}
                </ListItemButton>
              ))}
            </List>
          </div>
        </Drawer>
      </>
    );
  }

  return (
    <FormControl
      fullWidth
      error={props.error}
      required={props.required}
      size='small'>
      {props.label && <InputLabel id={`${generatedId}-label`}>{props.label}</InputLabel>}
      <Select
        id={generatedId}
        labelId={`${generatedId}-label`}
        size='small'
        label={props.label}
        value={selectedIndex !== undefined ? String(selectedIndex) : ''}
        onChange={handleChange}
        open={isOpen}
        displayEmpty
        renderValue={(value: string) => (value ? getOptionText(options[Number(value)]) : props.emptyText)}
        onClose={() => {
          setIsOpen(false);
        }}
        onClick={() => {
          if (options.length > 1) {
            setIsOpen(!isOpen);
          }
        }}
        {...(options.length <= 1 && singleOrEmptyProps)}
        sx={{
          textAlign: props.textAlign ?? 'left',
          '& fieldset': options.length === 1 ? { border: 'none' } : {},
          '& .Mui-disabled.MuiSelect-select.MuiInputBase-input':
            options.length === 1
              ? {
                  WebkitTextFillColor: theme.palette.text.primary,
                  paddingRight: '2px'
                }
              : {},
          backgroundColor: options.length > 1 ? theme.palette.common.white : 'default',
          width: props.width,
          color: !defaultValue ? theme.palette.grey[500] : theme.palette.common.black,
          fontStyle: !defaultValue ? 'italic' : 'none'
        }}>
        {options.map((item, index) => (
          <MenuItem
            key={`${generatedId}-option-${String(index)}`}
            value={String(index)}>
            {getOptionText(item)}
          </MenuItem>
        ))}
      </Select>
      {props.helperText && <FormHelperText id={`${generatedId}-helper-text`}>{props.helperText}</FormHelperText>}
    </FormControl>
  );
}
